import { useEffect, useRef, useState } from "react"
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material"
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Replay as RetryIcon,
} from "@mui/icons-material"
import { FileAnalysisEntry, useMultiFileAnalyze } from "../hooks/useChatAnalyze"
import { useSaveChatMutation } from "../../store/chatAnalyzeApi"
import { DropZone } from "./DropZone"
import type { ChatAnalyze } from "../interfaces"

interface UploadDialogProps {
  open: boolean
  onClose: () => void
  /** Файлы, переданные из mini drop zone — сразу запускают анализ при открытии */
  initialFiles?: File[]
}

function buildSaveRequest(
  entry: FileAnalysisEntry,
): ChatAnalyze.SaveChatRequest | null {
  const { result, fileHash } = entry
  if (!result) return null
  return {
    fileHash,
    chatMeta: {
      tgChatId: String(result.chatId),
      name: result.chatName,
      type: result.chatType,
    },
    statsJson: result,
    members: result.allUsers.map((u) => ({
      tgUserId: u.uid,
      name: u.name,
      isBot: u.isBot,
      msgCount: u.count,
    })),
  }
}

type SaveStatus = {
  label: string
  color: "success" | "default" | "error" | "info"
  saving?: boolean
  canRetry?: boolean
}

interface EnrichedEntry extends FileAnalysisEntry {
  idx: number
  saveStatus?: SaveStatus
}

// ── FileRow ───────────────────────────────────────────────────────────────────

interface FileRowProps {
  entry: EnrichedEntry
  onRetry: (idx: number) => void
}

function FileRow({ entry, onRetry }: FileRowProps) {
  const isRunning = entry.status === "analyzing"

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {(isRunning || entry.status === "pending") && (
          <CircularProgress size={14} />
        )}
        {entry.status === "done" && (
          <CheckIcon fontSize="small" color="success" />
        )}
        {entry.status === "error" && (
          <ErrorIcon fontSize="small" color="error" />
        )}
        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
          {entry.file.name}
        </Typography>
        {entry.saveStatus && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {entry.saveStatus.saving && <CircularProgress size={12} />}
            <Chip
              label={entry.saveStatus.label}
              size="small"
              color={entry.saveStatus.color}
              variant="outlined"
            />
            {entry.saveStatus.canRetry && (
              <Button
                size="small"
                startIcon={<RetryIcon fontSize="small" />}
                onClick={() => onRetry(entry.idx)}
                sx={{ minWidth: "auto", px: 0.5, py: 0 }}
              >
                Повторить
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      {isRunning && (
        <LinearProgress
          variant="determinate"
          value={entry.progress}
          sx={{ height: 4, borderRadius: 1 }}
        />
      )}
      {entry.status === "error" && (
        <Typography variant="caption" color="error">
          {entry.error}
        </Typography>
      )}
    </Stack>
  )
}

// ── UploadDialog ──────────────────────────────────────────────────────────────

export function UploadDialog({
  open,
  onClose,
  initialFiles,
}: UploadDialogProps) {
  const { entries, addFiles, reset } = useMultiFileAnalyze()
  const [saveChat] = useSaveChatMutation()
  const [saveStatuses, setSaveStatuses] = useState<Map<number, SaveStatus>>(
    new Map(),
  )
  const savedIdxRef = useRef<Set<number>>(new Set())
  // Prevent re-firing initialFiles on subsequent renders
  const initialFiredRef = useRef(false)

  // Kick off analysis for files dropped onto the mini zone before dialog opened
  useEffect(() => {
    if (!open) {
      initialFiredRef.current = false
      return
    }
    if (initialFiles && initialFiles.length > 0 && !initialFiredRef.current) {
      initialFiredRef.current = true
      addFiles(initialFiles)
    }
  }, [open, initialFiles, addFiles])

  // Auto-save each file once its analysis reaches "done"
  useEffect(() => {
    entries.forEach((entry, idx) => {
      if (entry.status !== "done") return
      if (savedIdxRef.current.has(idx)) return
      savedIdxRef.current.add(idx)
      triggerSave(idx, entry)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries])

  const triggerSave = (idx: number, entry: FileAnalysisEntry) => {
    const req = buildSaveRequest(entry)
    if (!req) return

    setSaveStatuses((prev) => {
      const next = new Map(prev)
      next.set(idx, { label: "Сохранение...", color: "info", saving: true })
      return next
    })

    saveChat(req)
      .unwrap()
      .then(() => {
        setSaveStatuses((prev) => {
          const next = new Map(prev)
          next.set(idx, { label: "Сохранено", color: "success" })
          return next
        })
      })
      .catch((err) => {
        const data = err?.data as ChatAnalyze.DuplicateAnalysisError | undefined
        if (err?.status === 409 && data?.analyzedAt) {
          const date = new Date(data.analyzedAt).toLocaleDateString("ru-RU")
          setSaveStatuses((prev) => {
            const next = new Map(prev)
            next.set(idx, { label: `Уже сохранён (${date})`, color: "default" })
            return next
          })
        } else {
          setSaveStatuses((prev) => {
            const next = new Map(prev)
            next.set(idx, {
              label: "Ошибка сохранения",
              color: "error",
              canRetry: true,
            })
            return next
          })
        }
      })
  }

  const handleRetry = (idx: number) => {
    const entry = entries[idx]
    if (!entry) return
    savedIdxRef.current.delete(idx)
    setSaveStatuses((prev) => {
      const next = new Map(prev)
      next.delete(idx)
      return next
    })
    savedIdxRef.current.add(idx)
    triggerSave(idx, entry)
  }

  const handleClose = () => {
    reset()
    savedIdxRef.current.clear()
    setSaveStatuses(new Map())
    onClose()
  }

  const isAnyRunning = entries.some(
    (e) => e.status === "pending" || e.status === "analyzing",
  )

  const enriched: EnrichedEntry[] = entries.map((e, idx) => ({
    ...e,
    idx,
    saveStatus: saveStatuses.get(idx),
  }))

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Добавить чаты в библиотеку</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DropZone variant="full" onFiles={addFiles} disabled={isAnyRunning} />

          {enriched.length > 0 && (
            <Stack spacing={1.5}>
              {enriched.map((entry) => (
                <FileRow key={entry.idx} entry={entry} onRetry={handleRetry} />
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  )
}
